import React from 'react';
import { Trans } from 'react-i18next';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { IS_APP_BUILD } from '../lib/buildTarget';

/* LAPIDA post-t7-2c (D1, #640): "LLEVALA A TU iPHONE" SALIO DEL PANEL.
 *
 * Carlos la saco del Panel: la barra lateral ya no monta `panel-lateral` y el Panel ya no monta las
 * dos `panel-lista`, para usuario e invitado, en la web y en la app. Aqui murieron esas dos
 * variantes (su tarjeta, su X de cerrar, el gate de ancho y alto por `lib/useEsAncho.js`, el QR de
 * 88 con el pie al lado y el recuerdo del cierre). Una variante que no sea `landing` ya no pinta
 * nada. El recuerdo del cierre (la llave `sb_appstore_card_cerrada` de `lib/recuerdoLocal`) queda
 * INERTE: nadie la lee ni la escribe, y lo que ya este guardado en un aparato no se borra.
 * La `landing` pinta byte a byte lo de antes. Lo de abajo es la historia de la TANDA 27. */

/* TANDA 27 (firmas #242 y #243) — «LLÉVALA A TU iPHONE», EN UN SOLO SITIO.
 * TANDA 27b (firma #244 + ajuste de #242) — el vestido, y el chip Pro.
 *
 * ── QUÉ PINTA, Y POR QUÉ SON DOS COSAS Y NO UNA ─────────────────────────────
 *
 * Un bloque que dice dos cosas distintas al mismo usuario web:
 *
 *   · #242 — QUE LA APP EXISTE, y cómo llegar a ella sin teclear nada: un QR a
 *     la ficha del App Store en pantalla ancha, un badge en pantalla angosta.
 *   · #243 — QUE LA APP FUNCIONA SIN SEÑAL. Es la razón por la que alguien la
 *     querría teniendo la web, y no se deduce de ningún otro sitio del producto.
 *
 * Van juntas porque separadas no se entienden: el QR sin el porqué es un código
 * de barras, y el porqué sin el QR es una promesa sin puerta.
 *
 * ── LAS TRES VARIANTES, Y POR QUÉ SON TRES Y NO DOS ─────────────────────────
 *
 * La TANDA 27 tenía dos (`landing` y `dashboard`) y Carlos las vio «metidas así
 * nomás»: la del Panel colgaba fija al fondo de la columna, robándole alto a la
 * lista de documentos en escritorio y estorbando en el teléfono. La 27b parte
 * esa en dos, porque el sitio correcto NO es el mismo en los dos anchos:
 *
 *   · `landing`        — un panel bajo el CTA del hero. Un solo componente que
 *                        se reacomoda por CSS.
 *   · `panel-lateral`  — en escritorio, en la barra lateral, encima de «Idioma».
 *                        Ahí no le quita nada a la lista de documentos.
 *   · `panel-lista`    — en el teléfono, DENTRO del scroll de documentos, después
 *                        del último. Se va con el scroll en vez de tapar nada.
 *
 * ── QUIÉN DECIDE EL ANCHO: CSS DENTRO, JS ENTRE ÁRBOLES ─────────────────────
 *
 * Dentro de un mismo bloque manda CSS (`hidden md:block` / `md:hidden`): sin
 * listeners, sin re-render, y se actualiza sola al girar el teléfono. Así se
 * decide QR-vs-badge en la landing, igual que en la T27.
 *
 * Las dos variantes del Panel viven en árboles distintos —la lateral en
 * `Sidebar.js`, la de lista en `Dashboard.js`— y ahí CSS no alcanza: dejaría las
 * DOS montadas con una en `display:none`, o sea dos títulos iguales y dos
 * botones de cerrar con el mismo `aria-label` para un lector de pantalla. Esa
 * decisión la toma `lib/useEsAncho.js` con `matchMedia`, Y LA TOMA ESTE
 * COMPONENTE, no sus anfitriones: ellos montan y ya. Es la única forma de que la
 * cuarta superficie que llegue no pueda olvidarse del gate.
 *
 * ── EL DETECTOR DE APP: POR QUÉ NO SE IMPORTA EL DE LA BODEGA ───────────────
 *
 * `IS_APP_BUILD` sí se importa de su fuente única (`lib/buildTarget`). El
 * detector de shell —`window.Capacitor?.isNativePlatform?.()`— se escribe aquí
 * en vez de importarse de `lib/warehouse/connection.js`, que es donde está
 * exportado, porque ese módulo arrastra SQLCipher entero y este componente vive
 * en la LANDING PÚBLICA. Es la misma decisión, por la misma razón, que ya
 * tomaron `lib/tokenStore.js:40`, `lib/offline/networkVerdict.js:103` y
 * `lib/haptics.js:43`: tres líneas repetidas cuestan menos que un chunk de base
 * de datos en el primer paint de la portada.
 *
 * ── LO QUE ESTE COMPONENTE NO HACE ──────────────────────────────────────────
 *
 *   · No pide nada al servidor. Ni un fetch.
 *   · No estrena ningún campo en Mongo. El cierre de la tarjeta vive en el
 *     aparato y en ningún otro sitio.
 *   · No dibuja el QR en tiempo de ejecución. Es un SVG estático generado una
 *     vez y commiteado (`public/qr-appstore.svg`), así que no hay dependencia
 *     nueva ni bytes de librería en el bundle.
 */

/** La ficha en el App Store. Un solo literal para todas las ramas. */
export const URL_APPSTORE = 'https://apps.apple.com/app/id6792611474';

/** El SVG estático, generado una vez y verificado decodificándolo. */
const QR_SRC = '/qr-appstore.svg';

/* LAPIDA post-t7-2c (D1, #640): murieron `LLAVE_CERRADA` (el recuerdo del cierre de la tarjeta
 * del Panel, hoy inerte en los aparatos que la guardaron) y `esDelPanel`. */

/** ¿Corremos dentro del shell nativo? Se evalúa POR LLAMADA y no al cargar el
 *  módulo, igual que sus tres hermanos: los tests instalan y quitan el global. */
function enAppNativa() {
  if (IS_APP_BUILD) return true;
  try {
    return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
}

/* TANDA 27c (firma #245) — AQUÍ VIVÍA EL CHIP «Pro», Y SE FUE.
 *
 * La 27b puso una pastilla naranja con corona DELANTE del aviso. Funcionaba,
 * pero un chip es una ETIQUETA: dice «esto es de la categoría Pro» y deja que el
 * lector adivine qué parte de la frase necesita Pro. La #245 lo sustituye por la
 * frase misma —«Con Pro, la app sigue funcionando…»— con la palabra en el
 * naranja de la marca. Se lee en vez de descifrarse, y de paso el aviso deja de
 * empezar con un elemento que compite con el título de la tarjeta.
 *
 * Con él se fueron su llave (`common:appstore.pro_chip`, ya retirada de los dos
 * locales) y el icono `Crown` del import. */

/** El badge del App Store. Es SVG en línea y no una imagen de Apple a propósito:
 *  no se puede hotlinkear el asset oficial, y una copia local sería un binario
 *  más que mantener. La forma es la de la pastilla negra con borde: nombre de la
 *  tienda, no logotipo registrado. */
function BadgeAppStore({ etiqueta }) {
  return (
    <a
      href={URL_APPSTORE}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={etiqueta}
      title={etiqueta}
      data-testid="appstore-badge"
      className="inline-flex items-center gap-2 rounded-xl border border-[#3a3a3a] bg-black px-4 min-h-[44px] text-white transition-colors hover:border-[#5a5a5a]"
    >
      <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      <span className="text-sm font-medium leading-tight">{etiqueta}</span>
    </a>
  );
}

/** El QR con su pie. Fondo BLANCO fijo y tinta oscura: los lectores de QR
 *  quieren contraste, así que este cuadro no sigue al tema oscuro del producto
 *  — es el único sitio del carril donde eso es lo correcto.
 *
 *  El pie va DEBAJO y el QR mide 110 px (la landing): hay columna de sobra.
 *  LAPIDA post-t7-2c (D1, #640): murio la forma AL LADO y de 88 px, la de la
 *  barra lateral del Panel. */
function CuadroQR({ pie }) {
  const lado = 110;
  return (
    <div
      className="flex flex-col items-center gap-2"
      data-testid="appstore-qr"
    >
      <img
        src={QR_SRC}
        alt=""
        aria-hidden="true"
        width={lado}
        height={lado}
        style={{ width: `${lado}px`, height: `${lado}px` }}
        className="shrink-0 rounded-lg bg-white p-1"
      />
      <p className="text-[13px] text-[#8c8c8c] leading-snug max-w-[190px] text-center">
        {pie}
      </p>
    </div>
  );
}


export default function AppStoreBloque({ variante = 'landing' }) {
  /* `useReadyTranslation` y no `useTranslation` a secas, por dos razones y las
     dos son de producción:
       · Es el hook que usan SUS ANFITRIONES (LandingPage.js:4, Dashboard.js:25,
         y desde la 27b también Sidebar.js). Un bloque que se inyecta en varias
         pantallas debe leer el idioma por el mismo camino que ellas.
       · Con `useSuspense: false`, `t()` devuelve LA LLAVE mientras el namespace
         se está bajando. Sin la bandera `ready`, este bloque pintaría
         literalmente «appstore.card_title» durante 100-200 ms en una carga fría.
     Cuando no está listo se devuelve `null` y no el `LoadingGate` del hook: éste
     es un bloque subordinado, y taparle la portada al usuario con un spinner de
     pantalla completa por un anuncio sería peor que no enseñarlo. */
  const { t, ready } = useReadyTranslation('common');
  /* LAPIDA post-t7-2c (D1, #640): murieron el gate de ancho y alto de las variantes del Panel
     (`useEsAncho` y `useAltoCorto`), el recuerdo del cierre y el punto (3) y (4) de abajo. */

  // (0) post-t7-2c (D1, #640): solo existe la landing; cualquier otra variante no pinta nada.
  if (variante !== 'landing') return null;
  // (1) Dentro de la app nativa esto no existe. Va ANTES que todo lo demás.
  if (enAppNativa()) return null;
  // (2) Sin traducciones no se pinta: mejor nada que una llave cruda.
  if (!ready) return null;

  const badge = <BadgeAppStore etiqueta={t('appstore.badge_alt')} />;

  /* TANDA 27c (firma #245) — «Pro» VA DENTRO DE LA FRASE, EN NARANJA.
   *
   * POR QUÉ `<Trans>` Y NO `t()` A SECAS: una palabra de la frase lleva otro
   * color, y eso es una etiqueta DENTRO del texto. Las tres alternativas que no
   * se usaron, y por qué:
   *
   *   · Partir la llave en tres (prefijo + «Pro» + sufijo) obligaría al
   *     traductor a conservar el orden de las piezas, y en otro idioma la
   *     palabra puede no ir al principio. Es justo el error que `<Trans>`
   *     existe para evitar.
   *   · `dangerouslySetInnerHTML` metería HTML del fichero de locales al DOM.
   *   · «Pro» en duro en el JSX rompería el candado A y dejaría la palabra
   *     fuera del sitio donde se traduce.
   *
   * LA FORMA NUMÉRICA (`<1>Pro</1>` + `components={{ 1: … }}`) NO SE INVENTA
   * AQUÍ: es la que el repo ya usa en producción —`GuestConversionPrompt.js:47`
   * y `Dashboard.js:2311`— y `dashboard.json` ya trae nueve llaves con `<1>…</1>`
   * dentro.
   *
   * SE PASA `t={t}` (precedente: `ContactPage.js:100`) para que la llave resuelva
   * por el MISMO traductor que el resto del bloque, que ya está atado al
   * namespace `common`. Sin él, `<Trans>` iría por el `defaultNS` global, que es
   * el mismo hoy pero no tiene por qué serlo mañana.
   *
   * `text-[#FF6B00]` es el naranja del producto EN TEXTO: el mismo del enlace de
   * comprar créditos (`Dashboard.js:2447`) y el de la insignia de plan
   * (`Dashboard.js:2290`). */
  const nota = (
    <p className="text-[13px] text-[#8c8c8c] leading-relaxed" data-testid="appstore-offline-note">
      <Trans
        i18nKey="appstore.offline_note"
        t={t}
        components={{ 1: <strong key="1" className="text-[#FF6B00] font-semibold" /> }}
      />
    </p>
  );

  const titulo = (testid, extra = '') => (
    <h3 className={`text-sm font-semibold text-white ${extra}`} data-testid={testid}>
      {t('appstore.card_title')}
    </h3>
  );

  /* LAPIDA post-t7-2c (D1, #640): aqui vivia LA TARJETA DEL PANEL en sus dos sitios (la lateral con
     el QR de 88 y la de la lista con el badge, las dos con su X de cerrar). */

  // ── LA LANDING ────────────────────────────────────────────────────────────
  //
  // UN SOLO ÁRBOL QUE SE REACOMODA, no dos árboles de los que CSS apaga uno.
  //
  // La primera versión de esta tanda tenía dos bloques hermanos —`hidden md:flex`
  // para el panel y `md:hidden` para el teléfono— y un test lo tumbó: eso deja
  // DOS badges en el DOM con el mismo `data-testid` y el mismo `aria-label`, que
  // es exactamente la regresión que la TANDA 27 ya había cazado una vez. Un
  // lector de pantalla no sabe cuál de los dos está mirando.
  //
  // Aquí hay UNA de cada cosa. Lo que cambia con el ancho es el VESTIDO del
  // contenedor (de nada a panel con borde) y qué piezas existen: el título y la
  // zona del QR llevan su propio gate, porque en el teléfono sobran los dos.
  return (
    <div className="mt-8 w-full max-w-[560px] mx-auto lg:mx-0" data-testid="appstore-landing">
      {/* EL VESTIDO ES TODO `md:`. En el teléfono este div no pinta NADA —ni
          borde, ni fondo, ni padding, ni fila— así que la landing angosta se ve
          exactamente como en la TANDA 27, que es lo que Carlos aprobó.
          De `md` para arriba copia BORDE y RADIO de la tarjeta de la pregunta
          del hero (`.lv-pcard`, index.css:732 — `1px solid #262626`, 20 px) para
          que sea de la misma familia, con el fondo un paso más discreto: aquélla
          es la protagonista de la portada y ésta no debe competirle. */}
      <div
        className="md:flex md:items-center md:gap-5 md:rounded-[20px] md:border md:border-[#262626] md:bg-white/[0.03] md:p-5"
        data-testid="appstore-landing-panel"
      >
        <div className="md:flex-1 md:min-w-0" data-testid="appstore-landing-izquierda">
          {/* Sólo en ancho: en el teléfono el bloque es un renglón de apoyo bajo
              el CTA, y un título lo convertiría en una sección que compite. */}
          <div className="hidden md:block">{titulo('appstore-landing-title')}</div>
          <div className="flex justify-center md:justify-start md:mt-3">{badge}</div>
          <div className="mt-3 md:mt-4">{nota}</div>
        </div>
        {/* Sólo en ancho: el QR y su divisor. En un teléfono el QR no sirve —lo
            tendría que escanear el mismo aparato que lo enseña— y el divisor sin
            nada a la derecha sería una raya suelta. */}
        <div
          className="hidden md:flex w-[200px] shrink-0 border-l border-[#262626] pl-5 flex-col items-center"
          data-testid="appstore-landing-derecha"
        >
          <CuadroQR pie={t('appstore.qr_caption')} />
        </div>
      </div>
    </div>
  );
}
